import React from 'react';
import { CalendarClock, X } from 'lucide-react';
import { daysFromToday, fmtYMD, addDaysYMD, toYMD } from './CustomerProfileModal';

/**
 * ─────────────────────────────────────────────────────────────
 *  Next Call (কতদিন পর কল দিতে হবে) — shared helper
 *
 *  Firestore:  orders/{id}.nextCall = { date: 'YYYY-MM-DD', status: 'pending'|'done',
 *                                        setBy, setAt, doneAt, doneBy }
 *  Call note সেভ করার সময় তারিখ দিলে এটা সেট হয়, Call Report → "Next Call Due" ট্যাবে দেখা যায়।
 * ─────────────────────────────────────────────────────────────
 */

export const NEXT_CALL_QUICK_DAYS = [1, 3, 7, 15, 30];

/** 'আজ' / 'কাল' / '৩ দিন পর' / '২ দিন দেরি' */
export const nextCallLabel = (ymd) => {
    const d = daysFromToday(ymd);
    if (d === null) return '';
    if (d < 0) return `${-d} দিন দেরি`;
    if (d === 0) return 'আজ';
    if (d === 1) return 'কাল';
    return `${d} দিন পর`;
};

/** লিস্টের Lead Status এর নিচে ছোট ব্যাজ */
export function NextCallBadge({ order }) {
    const nc = order?.nextCall;
    if (!nc || !nc.date) return null;

    if (nc.status === 'done') {
        return (
            <span className="inline-block text-xs px-2 py-0.5 rounded-full font-semibold bg-green-100 text-green-700">
                ✓ Call done
            </span>
        );
    }

    const d = daysFromToday(nc.date);
    const tone = d !== null && d < 0
        ? 'bg-red-100 text-red-700'
        : d === 0
            ? 'bg-orange-100 text-orange-700'
            : 'bg-blue-100 text-blue-700';

    return (
        <span
            className={`inline-block text-xs px-2 py-0.5 rounded-full font-semibold ${tone}`}
            title={`Next call: ${fmtYMD(nc.date)}`}
        >
            📞 {fmtYMD(nc.date)} · {nextCallLabel(nc.date)}
        </span>
    );
}

/** Note modal / Reschedule modal এ ব্যবহারের তারিখ বাছাই */
export function NextCallPicker({ value, onChange }) {
    return (
        <div className="mb-4">
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                <CalendarClock size={16} className="text-orange-500" />
                পরবর্তী কল কবে দিতে হবে? <span className="text-gray-400 font-normal">(Next Call Date, ঐচ্ছিক)</span>
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
                {NEXT_CALL_QUICK_DAYS.map((n) => {
                    const ymd = addDaysYMD(n);
                    const active = value === ymd;
                    return (
                        <button
                            key={n}
                            type="button"
                            onClick={() => onChange(ymd)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition ${active
                                ? 'bg-orange-400 text-white border-orange-400'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-orange-50'}`}
                        >
                            {n} দিন পর
                        </button>
                    );
                })}
            </div>
            <div className="flex items-center gap-2">
                <input
                    type="date"
                    value={value || ''}
                    min={toYMD(new Date())}
                    onChange={(e) => onChange(e.target.value)}
                    className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
                {value && (
                    <button
                        type="button"
                        onClick={() => onChange('')}
                        className="p-2 text-gray-500 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                        title="তারিখ মুছুন"
                    >
                        <X size={18} />
                    </button>
                )}
            </div>
            {value && (
                <p className="text-xs text-orange-600 mt-1.5">
                    {fmtYMD(value)} — {nextCallLabel(value)}
                </p>
            )}
        </div>
    );
}
